'use client';

import * as React from 'react';

import { ResourceBadge } from '@/components/ui/resource-badge';
import { ORG_ROUTES } from '@/config/router';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

interface PaymentFlowResourceBadgeProps {
  /** Payment Flow ID */
  flowId: string;
  /** Payment Flow name (optional, will use truncated ID if not provided) */
  flowName?: string | null;
  /** Badge variant */
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  /** Additional CSS classes */
  className?: string;
}

/**
 * PaymentFlowResourceBadge component for displaying payment flow information
 * with clickable link to the payment flow details page.
 * Payment flows are global (account-level), not store-specific.
 */
export function PaymentFlowResourceBadge({
  flowId,
  flowName,
  variant = 'secondary',
  className,
}: PaymentFlowResourceBadgeProps) {
  const { orgSlug } = useOrgNavigate();
  const orgRoutes = ORG_ROUTES(orgSlug);
  
  // Use flow name if available, otherwise use truncated flow ID
  const displayValue = React.useMemo(() => {
    return flowName || `${flowId.substring(0, 12)}...`;
  }, [flowName, flowId]);

  return (
    <ResourceBadge
      name="Payment Flow"
      value={displayValue}
      appLink={orgRoutes.PAYMENT_FLOW_DETAILS(flowId)}
      variant={variant}
      className={className}
    />
  );
}

